import React from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import "./PieChart.scss";

export interface PieChartDataItem {
  name: string;
  value: number;
  color: string;
  count?: number;
  [key: string]: any; // Index signature for Recharts compatibility
}

interface PieChartProps {
  data: PieChartDataItem[];
  title?: string;
  height?: number;
  outerRadius?: number;
  innerRadius?: number;
  showLegend?: boolean;
  showTooltip?: boolean;
  className?: string;
  legendPosition?: "bottom" | "right" | "top";
  chartType?: "pie" | "donut";
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  height = 240,
  outerRadius = 85,
  innerRadius = 0,
  showLegend = true,
  showTooltip = true,
  className = "",
  legendPosition = "bottom",
  chartType = "pie",
}) => {
  const actualInnerRadius =
    chartType === "donut" ? innerRadius || 45 : innerRadius;

  const renderCustomTooltip = (props: any) => {
    if (!props.active || !props.payload || !props.payload[0]) return null;

    const data = props.payload[0].payload;
    return (
      <div className="pie-chart__tooltip">
        <div className="pie-chart__tooltip-label">{data.name}</div>
        <div className="pie-chart__tooltip-value">
          {data.count ? `${data.count} ta` : `${data.value}%`}
        </div>
      </div>
    );
  };

  const renderLegend = () => {
    if (!showLegend) return null;

    return (
      <div className={`pie-chart__legend pie-chart__legend--${legendPosition}`}>
        {data.map((entry, index) => (
          <div key={index} className="pie-chart__legend-item">
            <div
              className="pie-chart__legend-color"
              style={{ background: entry.color }}
            />
            <div className="pie-chart__legend-text">
              <span className="pie-chart__legend-name">{entry.name}</span>
              <span className="pie-chart__legend-value">
                {entry.count
                  ? `${entry.count} ta (${entry.value}%)`
                  : `${entry.value}%`}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`pie-chart ${className}`}>
      {title && <div className="pie-chart__title">{title}</div>}

      <div className="pie-chart__container">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsPieChart>
            <defs>
              {data.map((entry, index) => (
                <linearGradient
                  key={index}
                  id={`gradient-${index}`}
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop offset="0%" stopColor={entry.color} />
                  <stop
                    offset="100%"
                    stopColor={entry.color}
                    stopOpacity={0.8}
                  />
                </linearGradient>
              ))}
              <filter
                id="pie-shadow"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feDropShadow
                  dx="0"
                  dy="3"
                  stdDeviation="6"
                  floodColor="rgba(0,0,0,0.25)"
                />
              </filter>
            </defs>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={outerRadius}
              innerRadius={actualInnerRadius}
              fill="#8884d8"
              dataKey="value"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={`url(#gradient-${index})`}
                  filter="url(#pie-shadow)"
                />
              ))}
            </Pie>
            {showTooltip && (
              <Tooltip
                content={renderCustomTooltip}
                animationDuration={300}
                animationEasing="ease-out"
              />
            )}
          </RechartsPieChart>
        </ResponsiveContainer>

        {renderLegend()}
      </div>
    </div>
  );
};

export default PieChart;
